import BaseController from "confinus_ui/base_controller";
import { showModal } from "lib/overlay/modal";
import { showNotification } from "lib/overlay/notification";
import DropdownController from "confinus_ui/dropdown_controller";
import { ConfinusUI__DropdownControllerIdentifier } from "controllers/identifiers";

type RefreshPopupMode = "inactive" | "countdown" | "show_immediately";

const ACTIVE_CLASS =
  "shopping__product-serviceability-dropdown__button--active";
const DROPDOWN_MINUTES_BEFORE_RUN = 60;
const TIMED_TOASTER_MINUTES_BEFORE_RUN = 30;
const TIMED_TOASTER_DISPLAY_UNTIL_IN_MINUTES = 11;
const DISMISSABLE_TOASTER_MINUTES_BEFORE_RUN = 10;
const DISMISSABLE_TOASTER_DISPLAY_UNTIL_IN_MINUTES = 1;
const REFRESH_POPUP_SHOW_IMMEDIATELY_DELAY = 2;

export default class ServiceabilityDropdownController extends BaseController {
  // Targets
  public static targets = ["dropdownButton"];

  public static values = {
    cartUid: { type: String, default: null },
    currentPage: { type: String, default: null },
    timeToCutOff: Number,
    refreshPopupMode: { type: String, default: null },
  };
  declare private readonly dropdownButtonTarget: HTMLElement;
  declare private readonly cartUidValue: any;
  declare private readonly currentPageValue: any;
  declare private readonly timeToCutOffValue: number | null;
  declare private readonly refreshPopupModeValue: any;

  // Outlets
  public static outlets = [ConfinusUI__DropdownControllerIdentifier];
  declare private readonly confinusUiDropdownOutlet: DropdownController;
  public get dropdownController() {
    return this.confinusUiDropdownOutlet;
  }

  private activateDropdownTimerId?: number;
  private deactivateDropdownTimerId?: number;
  private activateTimedToasterTimerId?: number;
  private activateDismissableToasterTimerId?: number;
  private popupTimerId?: number;

  public connect() {
    super.connect();

    const timeToCutOff = this.timeToCutOff;
    const refreshPopupMode = this.refreshPopupMode;

    if (timeToCutOff !== null && timeToCutOff > 0) {
      const timeToActivateDropdown =
        timeToCutOff - this.secondsBeforeRun(DROPDOWN_MINUTES_BEFORE_RUN);
      const timeToActivateTimedToaster =
        timeToCutOff - this.secondsBeforeRun(TIMED_TOASTER_MINUTES_BEFORE_RUN);
      const timeToActivateDismissableToaster =
        timeToCutOff -
        this.secondsBeforeRun(DISMISSABLE_TOASTER_MINUTES_BEFORE_RUN);

      this.activateTimedToasterTimerId = this.activateTimedToaster(
        timeToCutOff,
        this.calculateTimeout(timeToActivateTimedToaster),
      );
      this.activateDismissableToasterTimerId = this.activateDismissableToaster(
        timeToCutOff,
        this.calculateTimeout(timeToActivateDismissableToaster),
      );

      this.activateDropdownTimerId = this.activateDropdown(
        this.calculateTimeout(timeToActivateDropdown),
      );
      this.deactivateDropdownTimerId = this.deactivateDropdown(
        this.calculateTimeout(timeToCutOff),
      );

      this.popupTimerId = this.initialiseRefreshDialog(
        refreshPopupMode,
        timeToCutOff,
      );
    }
  }

  public disconnect() {
    clearTimeout(this.activateDropdownTimerId);
    clearTimeout(this.deactivateDropdownTimerId);
    clearTimeout(this.activateTimedToasterTimerId);
    clearTimeout(this.activateDismissableToasterTimerId);
    clearTimeout(this.popupTimerId);
    super.disconnect();
  }

  public toggle(event: Event) {
    this.dropdownController.toggle(event);
  }

  public hideOnClickOutside(event?: Event) {
    if (event && this.dropdownButtonTarget.contains(event.target as Node)) {
      return;
    }
    this.dropdownController.hideOnClickOutside(event);
  }

  public hide() {
    this.dropdownController.hide();
  }

  private initialiseRefreshDialog(
    refreshPopupMode: RefreshPopupMode,
    timeToCutOff: number,
  ) {
    if (refreshPopupMode === "inactive") {
      return;
    }

    const timeoutDelay =
      refreshPopupMode === "countdown"
        ? timeToCutOff
        : REFRESH_POPUP_SHOW_IMMEDIATELY_DELAY;
    return this.countdownRefreshPopup(this.calculateTimeout(timeoutDelay));
  }

  private activateDropdown = (timeout: number) => {
    if (timeout <= 0) {
      this.addActiveClass();
      return;
    }
    return window.setTimeout(this.addActiveClass, timeout);
  };

  private deactivateDropdown = (timeout: number) => {
    if (timeout <= 0) {
      return;
    }
    return window.setTimeout(this.removeActiveClass, timeout);
  };

  private activateTimedToaster = (timeToCutOff: number, timeout: number) => {
    if (timeout > 0) {
      return window.setTimeout(this.showTimedToaster, timeout);
    }
    if (
      timeToCutOff >
      this.secondsBeforeRun(TIMED_TOASTER_DISPLAY_UNTIL_IN_MINUTES)
    ) {
      return window.setTimeout(this.showTimedToaster, 2000);
    }
  };

  private activateDismissableToaster = (
    timeToCutOff: number,
    timeout: number,
  ) => {
    if (timeout > 0) {
      return window.setTimeout(this.showDismissableToaster, timeout);
    }
    if (
      timeToCutOff >
      this.secondsBeforeRun(DISMISSABLE_TOASTER_DISPLAY_UNTIL_IN_MINUTES)
    ) {
      return window.setTimeout(this.showDismissableToaster, 2000);
    }
  };

  private countdownRefreshPopup = (timeout: number) => {
    if (timeout <= 0) {
      return;
    }
    return window.setTimeout(this.showRefreshPopup, timeout);
  };

  private showTimedToaster = () => {
    showNotification({
      contentHref: "/marketplace/reminders/hurry-up",
      timeout: 30000,
    });
  };

  private showDismissableToaster() {
    showNotification({
      contentHref: "/marketplace/reminders/last-chance",
      timeout: Infinity,
    });
  }

  private calculateTimeout(timeoutInSeconds: number) {
    return timeoutInSeconds * 1000;
  }

  private secondsBeforeRun(minutesBeforeRun: number) {
    return minutesBeforeRun * 60;
  }

  private addActiveClass = () => {
    this.dropdownButtonTarget.classList.add(ACTIVE_CLASS);
  };

  private removeActiveClass = () => {
    this.dropdownButtonTarget.classList.remove(ACTIVE_CLASS);
  };

  private showRefreshPopup = () => {
    const ids = this.cartUidValue;
    const currentPage = this.currentPageValue;
    const href = `/marketplace/reminders/refresh-page?cart_encoded_id=${ids}&current_page=${currentPage}`;
    showModal({
      contentHref: href,
    });
  };

  private get timeToCutOff() {
    return this.timeToCutOffValue;
  }

  private get refreshPopupMode(): RefreshPopupMode {
    const refreshPopupMode = this.refreshPopupModeValue;
    switch (refreshPopupMode) {
      case "show_immediately":
      case "countdown":
        return refreshPopupMode;
      default:
        return "inactive";
    }
  }
}
